import { Identifier, KeycardInfo, LotSettings, SUREPARKSubscriber, TennatAccountContactInfo, VehicleInfo } from '../types';

export interface MatchingParkproContactsResult {
  matchingSureparkMembers: SUREPARKSubscriber[];
  completeSureparkMembers: SUREPARKSubscriber[];
  incompleteSureparkMembers: SUREPARKSubscriber[];
  missingSureparkMembers: SUREPARKSubscriber[];
}

/**
 * Matches SUREPARK subscribers with ParkPro contacts and categorizes them based on rate validation
 * This function is shared between frontend and backend for consistent matching logic
 *
 * @param locationId - The location ID
 * @param suite - The suite number
 * @param account - The account number
 * @param parkingDeviceGroupID - The parking device group ID to match against
 * @param allContacts - All ParkPro contacts for the tenant (optional, will throw error if not provided)
 * @param allSureparkMembers - All SUREPARK members/subscribers (optional, will throw error if not provided). These are all
 *                             the members from the surepark device - the filtering will occur in this function.
 * @param locationSettings - Location settings including rate information
 * @returns Categorized SUREPARK members based on matching and rate validation
 */
export function getMatchingParkproContacts(
  locationId: number,
  suite: string,
  account: string,
  parkingDeviceGroupID: string,
  allContacts?: TennatAccountContactInfo[],
  allSureparkMembers?: SUREPARKSubscriber[],
  locationSettings?: LotSettings,
  matchOnlyActiveSureparkMember: boolean = true
): MatchingParkproContactsResult {

    const fn = `getMatchingParkproContacts`;
    console.log(`${fn} entry. LocationId: ${locationId}, Suite: ${suite}, Account: ${account}, GroupId: ${parkingDeviceGroupID}`);

  // For first round of development, require optional parameters
  if (!allContacts) {
    throw new Error('allContacts parameter is required for the first version of this function');
  }

  if (!allSureparkMembers) {
    throw new Error('allSureparkMembers parameter is required for the first version of this function');
  }

  if (!locationSettings) {
    throw new Error('locationSettings parameter is required for the first version of this function');
  }

    if (!parkingDeviceGroupID) {
        throw new Error('No ParkPro group ID is supplied');
    }

  // Initialize result arrays
  const completeSureparkMembers: SUREPARKSubscriber[] = [];
  const incompleteSureparkMembers: SUREPARKSubscriber[] = [];
  const missingSureparkMembers: SUREPARKSubscriber[] = [];

  console.log(`Looking to see which SUREPARK members match ParkPro contacts for group ${parkingDeviceGroupID}`);

  // Filter SUREPARK members by matching groupId. We only want to find the members that are part of the same ParkPro company and SUREPARK group.
  const matchingSureparkMembers = allSureparkMembers.filter((member: SUREPARKSubscriber) => {
    return member.groupId === parkingDeviceGroupID;
  });

  // All of the SUREPARK members returned from the device.
  // console.log(`allSureparkMembers:`, allSureparkMembers);

  // All of the ParkPro contacts for the tenant.
  // console.log(`allContacts`, allContacts);

    // All of the SUREPARK group members who are part of the same ParkPro company
    //console.log(`matchingSureparkMembers`, matchingSureparkMembers);

  // Process each matching SUREPARK member
  matchingSureparkMembers.forEach((member: SUREPARKSubscriber) => {
    
    
      // Extract keycard and vehicle identifiers from SUREPARK member
      const keycardIdentifiers = member.identifiers.filter((identifier: Identifier) => identifier.type === 'Hid');
      const vehicleIdentifiers = member.identifiers.filter((identifier: Identifier) => identifier.type === 'PlateNumber');

      // Find matching ParkPro contact
    const matchedIndex = allContacts.findIndex((profileContact: TennatAccountContactInfo) => {
      if (member.identifiers.length) {
        // Check if at least one keycard matches
        const hasKeycardMatch = profileContact?.keycardsInfo?.some((ppidf: KeycardInfo) =>
          keycardIdentifiers.some((spidf: Identifier) =>
            spidf.identifierNumber === `${ppidf.facility_code}/${ppidf.keycards}`
          )
        );

        // Check if at least one vehicle matches
        const hasVehicleMatch = profileContact?.vehiclesInfo?.some((ppidf: VehicleInfo) =>
          vehicleIdentifiers.some((spidf: Identifier) =>
            spidf.identifierNumber === ppidf.plate
          )
        );

        return hasKeycardMatch || hasVehicleMatch;
      } else {
        
        /**
         * We dont have identifiers(keycard, vehicle) to find matching contact
         * So as fallback we are matching name
        **/
        return (profileContact.contactName == member.firstName+" "+member.lastName)
      }
    });

      if (matchedIndex !== -1) {
        // Contact found - check if rate is correct
        const matchedContact = allContacts[matchedIndex];

        // Rates do not need to match so this is a valid member at this point in the process.
        // ParkPro is the source of truth for the rate
        completeSureparkMembers.push(member);

        // // Check if the rate exists in approved rates
        // const matchedRateID = locationSettings.ratesReport?.approved?.findIndex((rate: any) =>
        //   rate['parkproCode'] === matchedContact['rate']
        // );
        //
        // if (matchedRateID !== -1) {
        //   // Rate is valid
        //   completeSureparkMembers.push(member);
        // } else {
        //   // Rate is invalid or missing
        //   incompleteSureparkMembers.push(member);
        // }

      } else {
        /**
         * If maych only active flag is TRUE and check if member is active
         * because It doesn’t make sense that we would warn if a SUREPARK account is inactive but missing.
        **/
        if(matchOnlyActiveSureparkMember) {
          if(member.active) {
            // No matching contact found
            missingSureparkMembers.push(member);
          }
        } else {
          // No matching contact found
          missingSureparkMembers.push(member);
        }
      }
    
  });

  // Log results for debugging (optional - only in environments where console is available)
  if (typeof console !== 'undefined' && console.log) {
    console.log(`Matching SUREPARK members for tenant ${suite}-${account}:`, {
      total: matchingSureparkMembers.length,
      complete: completeSureparkMembers.length,
      incomplete: incompleteSureparkMembers.length,
      missing: missingSureparkMembers.length
    });
  }

  return {
    matchingSureparkMembers,
    completeSureparkMembers,
    incompleteSureparkMembers,
    missingSureparkMembers
  };
}
